import type { Resource, Tool, ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';

import type { parseNaturalInput } from '@/_pure/naturalInput';

export { parseNaturalInput } from '@/_pure/naturalInput';

export function isToolHandleSelectable(
    toolHandle: ToolHandle,
    selectableToolHandleSet?: ReadonlySet<ToolHandle>,
): boolean {
    if (!selectableToolHandleSet) return true;
    return selectableToolHandleSet.has(toolHandle);
}

export function deriveToolInputRoleNames(tool: Tool): string[] {
    return Object.keys(tool.roleSpec.inputRoleByName);
}

export function deriveFirstInputRoleName(roleNames: readonly string[]): string | null {
    if (roleNames.length === 0) return null;
    const [firstRoleName] = roleNames;
    return typeof firstRoleName === 'string' && firstRoleName.length > 0
        ? firstRoleName
        : null;
}

export function deriveNextUnboundInputRoleName<TBinding>(args: {
    roleNames: readonly string[];
    inputBindings: Readonly<Record<string, TBinding | null>>;
    lastBoundRoleName?: string;
}): string | null {
    for (const roleName of args.roleNames) {
        if (!roleName) continue;
        if (args.inputBindings[roleName] === null) {
            return roleName;
        }
    }

    if (args.lastBoundRoleName && args.roleNames.includes(args.lastBoundRoleName)) {
        return args.lastBoundRoleName;
    }

    return deriveFirstInputRoleName(args.roleNames);
}

export function createDraftInputBindings<TBinding>(roleNames: readonly string[]): Record<string, TBinding | null> {
    return Object.fromEntries(roleNames.map((roleName) => [roleName, null]));
}

export function hasAllRequiredBindings<TBinding>(inputBindings: Readonly<Record<string, TBinding | null>>): boolean {
    return Object.values(inputBindings).every((value) => value !== null);
}

export function buildRuntimeNaturalResource(projection: number): Resource {
    return {
        id: `RESOURCE-NATURAL:${projection}`,
        resourceTypeHandle: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
        projection,
        provenance: {
            provenanceKind: 'runtime',
        },
    };
}

export function buildRuntimeBooleanResource(value: boolean): Resource {
    return {
        id: `RESOURCE-BOOLEAN:${value ? 'true' : 'false'}`,
        resourceTypeHandle: CONSTANTS.Handles.ResourceTypes.TYPE_Boolean,
        projection: value,
        provenance: {
            provenanceKind: 'runtime',
        },
    };
}
